import { Injectable } from '@angular/core';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { environment } from '../../environments/environment';
import { Api } from './api';
import { Router } from '@angular/router';
import { WebStorage } from './web-storage';
import { Sso } from './sso';

@Injectable({
  providedIn: 'root',
})
export class Auth {

  authConfig: AuthConfig = {
    issuer: 'https://accounts.google.com',
    scope: '',
    clientId: '',
    redirectUri: window.location.origin + (environment.folderName !== '' ? `/${environment.folderName}` : '') + '/',
    // responseType: 'token id_token code',
    strictDiscoveryDocumentValidation: false,
    responseType: 'code',
    showDebugInformation: true,
    disableAtHashCheck: true,
    useSilentRefresh: false,
    requireHttps: false,
    disablePKCE: true,
    customQueryParams: {
      access_type: 'offline', // This is how Google issues refresh tokens
      // prompt: 'consent', // Ensures refresh token is issued every time
    }
  };
  isAssociateIdLogin: boolean = false;
  
  constructor(private oauthService: OAuthService, private apiService: Api, private router: Router,
    private readonly webStorageService: WebStorage, private readonly ssoService: Sso
  ) {
  }

  initiateLogin() {
    this.setLogoutProcess(false);
    this.apiService.getAuthConfig().subscribe({
      next: (response:any) => {
        console.log('Auth Config from server:', response);
        if (response) {
          this.authConfig.clientId = response.client_id;
          this.authConfig.issuer = response.issuer;
          this.authConfig.scope = response.scope;
          this.authConfig.requireHttps = false;
          this.clearAuthenticationError();
          this.oauthService.configure(this.authConfig);
          this.oauthService.loadDiscoveryDocumentAndTryLogin();
          this.login();
        }
      },
      error: (e:any) => {
        const error = e?.message || e?.statusText;
        console.error('Error while fetching auth config:', error);
        this.setAuthenticationError('Unable to connect to server. Please try again later.');
        // window.location.href = `/login-error`;
        this.router.navigate(['/login-error']);
      }
    });
  }

  login() {
    console.log('Starting login process...');
    // this.oauthService.initLoginFlow();
    this.oauthService.loadDiscoveryDocument().then(() => {
      this.oauthService.initImplicitFlow();
    });
  }

  setEncryptionKey() {
    this.webStorageService.setKey(environment.sk);
  }

  logout(type: string = 'logout') {
    sessionStorage.clear();
    localStorage.clear();
    this.oauthService.logOut();
    this.oauthService.revokeTokenAndLogout();
    let url = '/login';
    if (type === 'logout') {
      this.setLogoutProcess(true);
      url = '/login';
    } else if (type === 'session-expired') {
      url = '/login-error';
    }
    // window.location.href = window.location.origin + (environment.folderName !== '' ? `/${environment.folderName}` : '') + url;
    this.router.navigate([url]);
  }

  initiateLogout() {
    if (['prod', 'uat'].includes(environment.name.toLowerCase())) {
      this.ssoService.$logout.next(true);
    } else {
      this.logout();
    }
  }

  setLogoutProcess(flag: boolean) {
    sessionStorage.setItem('isLoggingOut', flag.toString());
  }

  getLogoutProcess(): boolean {
    return sessionStorage.getItem('isLoggingOut') === 'true';
  }

  clearAuthenticationError() {
    sessionStorage.removeItem('authErrorMessage');
  }

  setAuthenticationError(message: string) {
    sessionStorage.setItem('authErrorMessage', message);
  }

  getAuthenticationError(): string {
    return sessionStorage.getItem('authErrorMessage') || '';
  }

  redirectToLogin() {
    // window.location.href = '/login';
    this.router.navigate(['/login']);
  }

  get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  get isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  get identityClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  set associateIdLogin(flag: boolean) {
    this.isAssociateIdLogin = flag;
  }

  getAccessToken(): any {
    const envName = environment.name.toLowerCase() || '';
    const itemStr = ['prod', 'uat'].includes(envName) || this.isAssociateIdLogin
      ? sessionStorage.getItem('_t') // this.webStorageService.retrieveItem('_t')
      : localStorage.getItem('_t');
    if (!itemStr) {
      console.log('Access token not found!.');
      return null;
    }
    return itemStr || null;
  }
  
  setAccessToken(token: string, ttl: number = 3540): void { // ttl = time-to-live in milliseconds
    localStorage.setItem('_t', token);
  }

  setUserDetails(userDetails: any): void {
    this.webStorageService.storeInLocal('user_details', userDetails);
  }

  getUserDetails(): any {
    const envName = environment.name.toLowerCase() || '';
    let userDetails = ['prod', 'uat'].includes(envName) || this.isAssociateIdLogin
      ? this.webStorageService.retrieveItem('cu')
      : this.webStorageService.fetchFromLocal('user_details');
    console.log('userDetails', userDetails);
    return userDetails ? userDetails : null;
  }
}
